/* ===== Offline Queue Service - Cloud Functions Queue ===== */
import { logger } from '../utils/logger.js?v=83.21';
import { showSnackbar } from '../utils/ui.js?v=83.21';
import { updatePendingPlayStatus } from './pending-plays.js?v=83.21';

function debugLog(...args) {
  // safe no-op logger (nu blocheaza executia)
  try { console.log('[offline-queue]', ...args); } catch (e) {}
}

const QUEUE_DB = 'offlineQueue';
const QUEUE_STORE = 'operations';
const QUEUE_LOCK_KEY = 'offlineQueue_lock';
const QUEUE_HEARTBEAT_INTERVAL = 2000; // 2 seconds
const QUEUE_LOCK_TIMEOUT = 10000; // 10 seconds - if heartbeat not updated, lock is stale
const FALLBACK_QUEUE_KEY = 'offlineQueue_fallback';
let queueLockHeartbeat = null;
let fallbackQueueCache = null;
let persistenceWarningShown = false;

function showPersistenceWarning() {
  if (persistenceWarningShown) return;
  persistenceWarningShown = true;
  showSnackbar('Offline limited: persistence unavailable');
}

function buildFallbackOpId(operation) {
  const data = {
    functionName: operation.functionName || '',
    args: operation.args || null,
    adminId: operation.adminId || '',
    createdAt: Math.floor(Date.now() / 1000)
  };
  const raw = JSON.stringify(data);
  let hash = 0;
  for (let i = 0; i < raw.length; i += 1) {
    hash = (hash * 31 + raw.charCodeAt(i)) % 1000000007;
  }
  return `op_${hash}`;
}

// ✅ FIX: Leader election for multi-tab queue processing
function acquireQueueLock() {
  try {
    const lockData = localStorage.getItem(QUEUE_LOCK_KEY);
    const now = Date.now();
    
    if (!lockData) {
      // No lock exists, acquire it
      const tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(QUEUE_LOCK_KEY, JSON.stringify({ tabId, timestamp: now }));
      
      // Start heartbeat
      queueLockHeartbeat = setInterval(() => {
        try {
          const currentLock = localStorage.getItem(QUEUE_LOCK_KEY);
          if (currentLock) {
            const lock = JSON.parse(currentLock);
            if (lock.tabId === tabId) {
              localStorage.setItem(QUEUE_LOCK_KEY, JSON.stringify({ tabId, timestamp: Date.now() }));
            }
          }
        } catch (e) {
          logger.warn('[OfflineQueue] Heartbeat update failed:', e);
        }
      }, QUEUE_HEARTBEAT_INTERVAL);
      
      return true;
    }
    
    // Lock exists, check if it's stale
    const lock = JSON.parse(lockData);
    if (now - lock.timestamp > QUEUE_LOCK_TIMEOUT) {
      // Lock is stale, acquire it
      const tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(QUEUE_LOCK_KEY, JSON.stringify({ tabId, timestamp: now }));
      
      // Start heartbeat
      if (queueLockHeartbeat) clearInterval(queueLockHeartbeat);
      queueLockHeartbeat = setInterval(() => {
        try {
          const currentLock = localStorage.getItem(QUEUE_LOCK_KEY);
          if (currentLock) {
            const lock = JSON.parse(currentLock);
            if (lock.tabId === tabId) {
              localStorage.setItem(QUEUE_LOCK_KEY, JSON.stringify({ tabId, timestamp: Date.now() }));
            }
          }
        } catch (e) {
          logger.warn('[OfflineQueue] Heartbeat update failed:', e);
        }
      }, QUEUE_HEARTBEAT_INTERVAL);
      
      return true;
    }
    
    // Lock is held by another tab
    return false;
  } catch (e) {
    logger.warn('[OfflineQueue] Lock acquisition failed:', e);
    return false; // Fail open - allow processing if lock fails
  }
}

function releaseQueueLock() {
  try {
    if (queueLockHeartbeat) {
      clearInterval(queueLockHeartbeat);
      queueLockHeartbeat = null;
    }
    localStorage.removeItem(QUEUE_LOCK_KEY);
  } catch (e) {
    logger.warn('[OfflineQueue] Lock release failed:', e);
  }
}

// ✅ FIX: Listen for lock changes (other tab released or died)
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === QUEUE_LOCK_KEY && !e.newValue) {
      // Lock was released, try to process queue
      logger.info('[OfflineQueue] Lock released by other tab, attempting to process queue');
      setTimeout(processQueue, 100);
    }
  });
  
  // Release lock on page unload
  window.addEventListener('beforeunload', () => {
    releaseQueueLock();
  });
}

function getFallbackQueue() {
  if (fallbackQueueCache) return [...fallbackQueueCache];
  if (typeof localStorage === 'undefined') {
    fallbackQueueCache = [];
    showPersistenceWarning();
    return [];
  }
  try {
    const stored = localStorage.getItem(FALLBACK_QUEUE_KEY);
    fallbackQueueCache = stored ? JSON.parse(stored) : [];
  } catch (e) {
    logger.warn('[OfflineQueue] Failed to read fallback queue:', e);
    fallbackQueueCache = [];
    showPersistenceWarning();
  }
  return [...fallbackQueueCache];
}

function setFallbackQueue(queue) {
  fallbackQueueCache = [...queue];
  if (typeof localStorage === 'undefined') {
    showPersistenceWarning();
    return;
  }
  try {
    localStorage.setItem(FALLBACK_QUEUE_KEY, JSON.stringify(fallbackQueueCache));
  } catch (e) {
    logger.warn('[OfflineQueue] Failed to persist fallback queue:', e);
    showPersistenceWarning();
  }
}

// Initialize IndexedDB
function initDB() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'));
      return;
    }
    // ✅ FIX: Bump DB version to add opId index
    const request = indexedDB.open(QUEUE_DB, 2);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (e) => {
      const db = e.target?.result;
      const tx = e.target?.transaction;
      if (!db || !tx) return;
      const oldVersion = e.oldVersion || 0;
      
      // Create store if it doesn't exist (version 1)
      if (!db.objectStoreNames.contains(QUEUE_STORE)) {
        const store = db.createObjectStore(QUEUE_STORE, { keyPath: 'id', autoIncrement: true });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('status', 'status', { unique: false });
      }
      
      // ✅ FIX: Migration from version 1 to 2 - add opId index for deduplication
      if (oldVersion < 2) {
        const store = tx.objectStore(QUEUE_STORE);
        // Check if opId index already exists (idempotent migration)
        if (!store.indexNames.contains('opId')) {
          store.createIndex('opId', 'opId', { unique: false });
          debugLog('log', '[OfflineQueue] Created opId index for deduplication');
        }
      }
    };
  });
}

// Add operation to queue
export async function queueOperation(operation) {
  // ✅ FIX: Generate opId upfront for deduplication (both IndexedDB and fallback)
  const opId = buildFallbackOpId(operation);
  
  try {
    const db = await initDB();
    const tx = db.transaction(QUEUE_STORE, 'readwrite');
    const store = tx.objectStore(QUEUE_STORE);
    
    // ✅ FIX: Check for existing pending operation with same opId (idempotency)
    try {
      const opIdIndex = store.index('opId');
      const existingOps = await new Promise((resolve, reject) => {
        const request = opIdIndex.getAll(opId);
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      });
      
      // Filter to only pending operations (same opId with different status is OK)
      const pendingDuplicate = existingOps.find(op => op.status === 'pending' && op.opId === opId);
      if (pendingDuplicate) {
        logger.info('[OfflineQueue] Operation already queued (idempotent skip):', opId);
        return; // Idempotent: don't queue duplicate
      }
    } catch (dedupeError) {
      // If dedupe check fails, log warning but continue (fail open)
      logger.warn('[OfflineQueue] Failed to check for duplicates, queueing anyway:', dedupeError);
    }
    
    const queueItem = {
      ...operation,
      opId: opId, // ✅ FIX: Always include opId for deduplication
      timestamp: Date.now(),
      status: 'pending',
      retries: 0
    };
    
    await new Promise((resolve, reject) => {
      const request = store.add(queueItem);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    logger.info('[OfflineQueue] Operation queued:', queueItem);
    
    // Try to process immediately if online
    if (navigator.onLine) {
      setTimeout(processQueue, 100);
    }
  } catch (error) {
    logger.warn('[OfflineQueue] Falling back to localStorage queue:', error);
    const queueItem = {
      ...operation,
      opId: opId, // ✅ FIX: Use same opId for fallback deduplication
      id: `fallback_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      timestamp: Date.now(),
      status: 'pending',
      retries: 0
    };
    const queue = getFallbackQueue();
    // ✅ FIX: Fallback already has deduplication (existing code is correct)
    const existingIndex = queue.findIndex(item => item.opId === queueItem.opId && item.status === 'pending');
    if (existingIndex === -1) {
      queue.push(queueItem);
    } else {
      // Update existing pending operation (idempotent)
      queue[existingIndex] = { ...queue[existingIndex], ...queueItem };
      logger.info('[OfflineQueue] Updated existing pending operation in fallback queue:', opId);
    }
    setFallbackQueue(queue);
    if (navigator.onLine) {
      setTimeout(processQueue, 100);
    }
  }
}

// Process queue
export async function processQueue() {
  if (!navigator.onLine) {
    logger.info('[OfflineQueue] Offline, skipping queue processing');
    return;
  }
  
  // ✅ FIX: Acquire lock before processing (prevent multi-tab duplicates)
  if (!acquireQueueLock()) {
    logger.info('[OfflineQueue] Queue processing locked by another tab, skipping');
    return;
  }
  
  try {
    let store = null;
    let operations = [];
    let usingFallback = false;

    try {
      const db = await initDB();
      const tx = db.transaction(QUEUE_STORE, 'readwrite');
      store = tx.objectStore(QUEUE_STORE);
      const index = store.index('status');
      operations = await new Promise((resolve, reject) => {
        const request = index.getAll('pending');
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    } catch (dbError) {
      usingFallback = true;
      operations = getFallbackQueue().filter((op) => op.status === 'pending');
      logger.warn('[OfflineQueue] Using fallback queue for processing:', dbError);
    }
    
    if (operations.length === 0) {
      releaseQueueLock();
      return;
    }
    
    logger.info(`[OfflineQueue] Processing ${operations.length} queued operations (lock acquired)`);
    
    let hasCompleted = false;
    const updateOperation = async (op) => {
      if (usingFallback) {
        const queue = getFallbackQueue();
        const index = queue.findIndex(item => item.id === op.id);
        if (index >= 0) {
          queue[index] = { ...op };
          setFallbackQueue(queue);
        }
        return;
      }
      await new Promise((resolve, reject) => {
        const request = store.put(op);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    };
    
    for (const op of operations) {
      try {
        // Import function dynamically
        const module = await import('./backend.js?v=83.21');
        const func = module[op.functionName];
        
        if (!func) {
          throw new Error(`Function ${op.functionName} not found`);
        }
        
        // ✅ FIX: Pentru updateGameStatsBackend, adaugă flag fromQueue: true (+ publicSlug dacă există)
        let args = op.args || [];
        if (op.functionName === 'updateGameStatsBackend') {
          const updates = args[0];
          const extra = args[1] && typeof args[1] === 'object' ? { ...args[1] } : {};
          extra.fromQueue = true;
          args = [updates, extra];
        }
        
        // Execute function with arguments (modified cu fromQueue dacă e cazul)
        const result = await func(...args);

        if (op.functionName === 'updateGameStatsBackend') {
          const updates = op.args?.[0] || {};
          if (updates.playId && updates.prizeId) {
            const status = result && result.success === true ? 'confirmed' : 'rejected';
            const message = status === 'confirmed'
              ? 'Recompensa a fost confirmată.'
              : 'Recompensa nu a putut fi validată. Stoc epuizat sau conflict.';
            
            await updatePendingPlayStatus(updates.playId, status, {
              message,
              result: result || null
            });
            
            if (typeof window !== 'undefined' && typeof window.handlePendingWinReconciliation === 'function') {
              const handled = window.handlePendingWinReconciliation({
                playId: updates.playId,
                status,
                message
              });
              if (!handled) {
                showSnackbar(message, status === 'confirmed' ? 'success' : 'error');
              }
            } else {
              showSnackbar(message, status === 'confirmed' ? 'success' : 'error');
            }
          }
        }
        
        // Mark as completed
        op.status = 'completed';
        hasCompleted = true; // ✅ Track if at least one operation completed
        await updateOperation(op);
        
        logger.info('[OfflineQueue] Operation completed:', op.id);
        
      } catch (error) {
        logger.error('[OfflineQueue] Operation failed:', op.id, error);
        
        // Retry logic (max 3 retries)
        op.retries = (op.retries || 0) + 1;
        if (op.retries >= 3) {
          op.status = 'failed';
          op.error = error.message;
          await updateOperation(op);
          showSnackbar(`Eroare: ${op.functionName} a eșuat după 3 încercări`);
        } else {
          // Retry later
          op.status = 'pending';
          await updateOperation(op);
        }
      }
    }
    
    // ✅ FIX: DUPĂ ce queue-ul a procesat operațiile, sincronizează setările
    // Dacă cel puțin un operation a fost completed, reîncarcă setările din Firestore
    // (după un delay pentru a permite Cloud Functions să finalizeze scrierea)
    if (hasCompleted) {
      try {
        const { getCurrentUser } = await import('./auth.js?v=83.21');
        const currentUser = getCurrentUser();
        
        if (currentUser) {
          const { loadSettings } = await import('./settings.js?v=83.21');
          // Delay 1-2 secunde pentru a permite Cloud Functions să finalizeze sync-ul în Firestore
          setTimeout(async () => {
            await loadSettings(currentUser.uid, true);
            logger.info('[OfflineQueue] Settings synchronized after queue processing');
          }, 1500); // 1.5 secunde delay
        }
      } catch (err) {
        // Non-critical: dacă sync eșuează, nu blochează aplicația
        logger.warn('[OfflineQueue] Failed to sync settings after queue processing:', err);
      }
    }
    
  } catch (error) {
    logger.error('[OfflineQueue] Error processing queue:', error);
  } finally {
    // ✅ FIX: Always release lock after processing
    releaseQueueLock();
  }
}

// Clear completed operations
export async function clearCompletedOperations() {
  try {
    try {
      const db = await initDB();
      const tx = db.transaction(QUEUE_STORE, 'readwrite');
      const store = tx.objectStore(QUEUE_STORE);
      const index = store.index('status');

      const operations = await new Promise((resolve, reject) => {
        const request = index.getAll('completed');
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      // Keep only last 100 completed operations
      if (operations.length > 100) {
        const toDelete = operations.slice(0, operations.length - 100);
        for (const op of toDelete) {
          await new Promise((resolve, reject) => {
            const request = store.delete(op.id);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
          });
        }
      }
    } catch (dbError) {
      const queue = getFallbackQueue();
      const completed = queue.filter((op) => op.status === 'completed');
      if (completed.length > 100) {
        const keep = completed.slice(completed.length - 100);
        const pending = queue.filter((op) => op.status !== 'completed');
        setFallbackQueue([...pending, ...keep]);
      }
      logger.warn('[OfflineQueue] Failed to clear completed operations via IndexedDB:', dbError);
    }
  } catch (error) {
    logger.error('[OfflineQueue] Error clearing completed operations:', error);
  }
}

// Initialize: process queue on load and when online
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    logger.info('[OfflineQueue] Connection restored, processing queue');
    processQueue();
    showSnackbar('Conexiune restaurată - sincronizare în curs...');
  });
  
  // Process queue on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(processQueue, 1000);
    });
  } else {
    setTimeout(processQueue, 1000);
  }
  
  // Clean up completed operations periodically
  setInterval(clearCompletedOperations, 60000); // Every minute
}
